/**
 * Small field primitives for editing a block in place on the canvas.
 *
 * They keep a local draft while the user types and commit on blur or Enter, so
 * a keystroke doesn't push a graph update per character — every commit is a
 * round trip to the service.
 *
 * The draft is seeded once and the whole control is remounted (`key`) when the
 * committed value changes underneath it, rather than synchronised with an
 * effect. Committing happens on blur, so the remount never steals focus.
 *
 * Every control carries React Flow's `nodrag nopan` classes — without them,
 * dragging to select text pans the canvas instead.
 */

import { Input, InputNumber, Select } from 'antd';
import React, { useState } from 'react';

const NO_DRAG = 'nodrag nopan';

export const AOPFieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<span className="footnoteregular uppercase tracking-wide text-character-secondary">
		{children}
	</span>
);

/* -------------------------------------------------------------------------- */
/*                                  Text field                                */
/* -------------------------------------------------------------------------- */

interface AOPTextFieldProps {
	value: string;
	onCommit: (next: string) => void;
	placeholder?: string;
	ariaLabel?: string;
	className?: string;
	disabled?: boolean;
	/** Draws the field in danger and tells a screen reader it was refused. */
	invalid?: boolean;
	/**
	 * Why the value was refused, when the caller decides it.
	 *
	 * Setting it draws the field in danger and prints the reason under it. For
	 * a rule about the value itself, prefer `validate` — it keeps the refusal
	 * with the field it is about.
	 */
	error?: string | null;
	/**
	 * Checks a value before it is committed: a message refuses it, null accepts.
	 *
	 * The refusal is the field's own state, which is the point. Held by the
	 * caller it needs an identity — which step, which escalation — and every
	 * identity we tried was wrong in a different way: an index moved when a
	 * reminder was deleted, an object was replaced when another of its fields
	 * changed, and a component without a key carried one block's complaint onto
	 * the next. The field has no such problem: it is already the thing the text
	 * belongs to, and it clears when the text does.
	 */
	validate?: (value: string) => string | null;
	/**
	 * The value came back to what was saved, without a commit.
	 *
	 * `commit` runs only when the draft differs from the saved value, so a
	 * refusal raised on an empty field is not cleared by typing the original
	 * text back — the field falls silent exactly when the author has fixed it.
	 * Escape does the same thing, which is why both report here.
	 */
	onRevert?: () => void;
}

const TextFieldInput: React.FC<AOPTextFieldProps> = ({
	value,
	onCommit,
	placeholder,
	ariaLabel,
	className,
	disabled,
	invalid,
	error,
	validate,
	onRevert,
}) => {
	const [draft, setDraft] = useState(value);
	/** The field's own refusal, about the text it is holding. */
	const [refusal, setRefusal] = useState<string | null>(null);
	const shown = error !== null && error !== undefined ? error : refusal;
	const isRefused = invalid === true || shown !== null;

	const commit = () => {
		if (draft === value) {
			// Nothing to send — and the text is back to what was saved, so a
			// refusal about what it used to be has nothing left to describe.
			setRefusal(null);

			if (onRevert) {
				onRevert();
			}

			return;
		}

		const refused = validate ? validate(draft) : null;

		if (refused !== null) {
			// Kept on screen rather than sent: the service would refuse the whole
			// document and say nothing about which field or which rule.
			setRefusal(refused);
			return;
		}

		setRefusal(null);
		onCommit(draft);
	};

	const field = (
		<Input
			size="small"
			disabled={disabled}
			aria-label={ariaLabel}
			placeholder={placeholder}
			value={draft}
			onChange={(event) => setDraft(event.target.value)}
			onBlur={commit}
			onPressEnter={(event) => event.currentTarget.blur()}
			onKeyDown={(event) => {
				if (event.key === 'Escape') {
					setDraft(value);
					setRefusal(null);

					if (onRevert) {
						onRevert();
					}

					event.currentTarget.blur();
				}
			}}
			aria-invalid={isRefused ? true : undefined}
			className={`${NO_DRAG} ${isRefused ? 'aop-field-invalid' : ''} ${className ? className : ''}`}
		/>
	);

	return shown ? (
		<span className="flex flex-col">
			{field}
			<span className="footnoteregular mt-2xs block text-character-danger">{shown}</span>
		</span>
	) : (
		field
	);
};

export const AOPTextField: React.FC<AOPTextFieldProps> = (props) => (
	<TextFieldInput key={props.value} {...props} />
);

/* -------------------------------------------------------------------------- */
/*                                  Text area                                 */
/* -------------------------------------------------------------------------- */

interface AOPTextAreaProps {
	value: string;
	onCommit: (next: string) => void;
	placeholder?: string;
	ariaLabel?: string;
	rows?: number;
	disabled?: boolean;
	/** Draws the field in danger and tells a screen reader it was refused. */
	invalid?: boolean;
	/**
	 * Why the value was refused, when the caller decides it.
	 *
	 * Setting it draws the field in danger and prints the reason under it. For
	 * a rule about the value itself, prefer `validate` — it keeps the refusal
	 * with the field it is about.
	 */
	error?: string | null;
	/**
	 * Checks a value before it is committed: a message refuses it, null accepts.
	 *
	 * The refusal is the field's own state, which is the point. Held by the
	 * caller it needs an identity — which step, which escalation — and every
	 * identity we tried was wrong in a different way: an index moved when a
	 * reminder was deleted, an object was replaced when another of its fields
	 * changed, and a component without a key carried one block's complaint onto
	 * the next. The field has no such problem: it is already the thing the text
	 * belongs to, and it clears when the text does.
	 */
	validate?: (value: string) => string | null;
	/**
	 * The value came back to what was saved, without a commit.
	 *
	 * `commit` runs only when the draft differs from the saved value, so a
	 * refusal raised on an empty field is not cleared by typing the original
	 * text back — the field falls silent exactly when the author has fixed it.
	 * Escape does the same thing, which is why both report here.
	 */
	onRevert?: () => void;
}

const TextAreaInput: React.FC<AOPTextAreaProps> = ({
	value,
	onCommit,
	placeholder,
	ariaLabel,
	rows,
	disabled,
	invalid,
	error,
	validate,
	onRevert,
}) => {
	const [draft, setDraft] = useState(value);
	/** The field's own refusal, about the text it is holding. */
	const [refusal, setRefusal] = useState<string | null>(null);
	const shown = error !== null && error !== undefined ? error : refusal;
	const isRefused = invalid === true || shown !== null;

	const commit = () => {
		if (draft === value) {
			setRefusal(null);

			if (onRevert) {
				onRevert();
			}

			return;
		}

		const refused = validate ? validate(draft) : null;

		if (refused !== null) {
			setRefusal(refused);
			return;
		}

		setRefusal(null);
		onCommit(draft);
	};

	const field = (
		<Input.TextArea
			disabled={disabled}
			aria-label={ariaLabel}
			placeholder={placeholder}
			rows={rows ? rows : 2}
			value={draft}
			onChange={(event) => setDraft(event.target.value)}
			onBlur={commit}
			onKeyDown={(event) => {
				if (event.key === 'Escape') {
					setDraft(value);
					setRefusal(null);

					if (onRevert) {
						onRevert();
					}

					event.currentTarget.blur();
				}
			}}
			aria-invalid={isRefused ? true : undefined}
			className={`${NO_DRAG} ${isRefused ? 'aop-field-invalid' : ''}`}
		/>
	);

	return shown ? (
		<span className="flex flex-col">
			{field}
			<span className="footnoteregular mt-2xs block text-character-danger">{shown}</span>
		</span>
	) : (
		field
	);
};

export const AOPTextArea: React.FC<AOPTextAreaProps> = (props) => (
	<TextAreaInput key={props.value} {...props} />
);

/* -------------------------------------------------------------------------- */
/*                                   Select                                   */
/* -------------------------------------------------------------------------- */

export interface AOPSelectOption {
	value: string;
	label: string;
	/**
	 * Listed but not choosable — a value the plan language does not accept yet.
	 * Shown rather than hidden so an author can see where the vocabulary is
	 * going instead of wondering whether we forgot it.
	 */
}

interface AOPSelectProps {
	value: string;
	options: AOPSelectOption[];
	onChange: (next: string) => void;
	ariaLabel?: string;
	className?: string;
	disabled?: boolean;
	/** Shown when `value` is empty — e.g. an exit pointing at no step. */
	placeholder?: string;
}

/**
 * A plain antd Select over a fixed, in-memory option list. This is NOT for
 * server-backed entities — pickers over suppliers, problem types or locations
 * must use `OWAsyncSelect` so they page and search server-side.
 */
export const AOPSelect: React.FC<AOPSelectProps> = ({
	value,
	options,
	onChange,
	ariaLabel,
	className,
	disabled,
	placeholder,
}) => (
	<Select
		size="small"
		disabled={disabled}
		placeholder={placeholder}
		aria-label={ariaLabel}
		value={value}
		onChange={onChange}
		className={`${NO_DRAG} w-full ${className ? className : ''}`}
		getPopupContainer={(trigger) => (trigger.parentElement ? trigger.parentElement : document.body)}
	>
		{options.map((option) => (
			<Select.Option key={option.value} value={option.value}>
				{option.label}
			</Select.Option>
		))}
	</Select>
);

/* -------------------------------------------------------------------------- */
/*                                   Number                                   */
/* -------------------------------------------------------------------------- */

interface AOPNumberFieldProps {
	value: number;
	onCommit: (next: number) => void;
	min?: number;
	max?: number;
	ariaLabel?: string;
	disabled?: boolean;
}

export const AOPNumberField: React.FC<AOPNumberFieldProps> = ({
	value,
	onCommit,
	min,
	max,
	ariaLabel,
	disabled,
}) => (
	<InputNumber
		size="small"
		disabled={disabled}
		aria-label={ariaLabel}
		min={min !== undefined ? min : 1}
		max={max}
		value={value}
		onChange={(next) => {
			if (typeof next === 'number' && !isNaN(next)) {
				onCommit(next);
			}
		}}
		className={`${NO_DRAG} w-20`}
	/>
);
